/* ============================================================
   GANTT CHART — Цифровой ДИАК ЗФ
   Общий Гант: клик по колбаске или чеврону раскрывает под строкой
   «врезку» — задачи соответствующего проекта отображаются мини-барами
   на той же кварталной шкале, что и родитель. Родительская колбаска
   подсвечивается как «фокус», дашедные штрихи от её краёв спускаются
   в область задач, чтобы было видно, как план помещается внутри интервала.
   Клик по мини-бару уводит на карточку проекта с deep-link на задачу.
   ============================================================ */

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  GripVertical,
  Trash2,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { type GanttItem } from "@/data/projects";
import { useProjects } from "@/contexts/ProjectContext";
import {
  roadmapPhasesSorted,
  compareGanttTasksDisplayOrder,
  type EditableProject,
  type EditableGanttTask,
} from "@/store/projectStore";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ─────────────────────────── visual tokens ───────────────────────────── */

/** Приглушённые цвета полос общего ганта — меньше насыщенности, лучше читаемость. */
const STATUS_COLORS: Record<string, string> = {
  active: "hsl(158 38% 38%)",
  planned: "hsl(215 42% 46%)",
  done: "hsl(215 14% 52%)",
};

const STATUS_LABELS: Record<string, string> = {
  active: "В работе",
  planned: "Запланировано",
  done: "Завершено",
};

/** Цвет «фактической» полосы — приглушённый оранжевый (см. рендер факт-бара). */
const FACT_BAR_COLOR = "rgba(202, 132, 64, 0.92)";
const FACT_BAR_BORDER = "rgba(155, 96, 40, 0.7)";

/** Цвета для светофора проектных задач во врезке (совпадают с EditableGantt). */
const INSET_STATUS_COLORS: Record<EditableGanttTask["status"], string> = {
  active: "#00A651",
  behind: "#E85D04",
  planned: "#0055A5",
  done: "#8B9BB4",
};

const INSET_STATUS_LABELS: Record<EditableGanttTask["status"], string> = {
  active: "В работе",
  behind: "Сдвиг сроков",
  planned: "Запланировано",
  done: "Выполнено",
};

function phaseRowBackground(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return "rgba(0,85,165,0.05)";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},0.04)`;
}

/* ─────────────────────────── timeline math ─────────────────────────── */

function parseDate(s: string) {
  return new Date(s);
}

function daysBetween(a: Date, b: Date) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

const CHART_START = new Date("2026-01-01");
const CHART_END = new Date("2027-12-31");
const TOTAL_DAYS = daysBetween(CHART_START, CHART_END);

/**
 * Текущая дата (нормализованная до полуночи в локальном времени, чтобы
 * positioning не дрожал в течение дня). Возвращается отдельно от useMemo —
 * используем её вместе с `useEffect`-таймером ниже, чтобы линия «сегодня»
 * автоматически перепрыгнула на следующие сутки, если страница открыта
 * долго.
 */
function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

const TODAY_MONTH_LABELS = [
  "янв",
  "фев",
  "мар",
  "апр",
  "мая",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
] as const;

function formatTodayLabel(d: Date): string {
  return `${d.getDate()} ${TODAY_MONTH_LABELS[d.getMonth()]}`;
}

const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
] as const;

function formatTodayLong(d: Date): string {
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()}`;
}

const GANTT_LABEL_COL_WIDTH = 320;
const GANTT_TIMELINE_MIN_WIDTH = 560;
const BAR_HEIGHT_PX = 12;
const BAR_HEIGHT_EXPANDED_PX = 16;
/** Зазор между плановой и фактической колбасками, когда они рисуются парой. */
const GANTT_PAIR_GAP = 3;
/**
 * Минимальная ширина колбаски (px), при которой внутри неё помещаются обе
 * подписи дат «дд.мм … дд.мм» без наложения. Уже — подписи скрываем (даты
 * остаются в тултипе).
 */
const GANTT_BAR_LABEL_MIN_PX = 80;

function getQuarters() {
  const labels = [
    { label: "Q1 2026", start: "2026-01-01", end: "2026-03-31" },
    { label: "Q2 2026", start: "2026-04-01", end: "2026-06-30" },
    { label: "Q3 2026", start: "2026-07-01", end: "2026-09-30" },
    { label: "Q4 2026", start: "2026-10-01", end: "2026-12-31" },
    { label: "Q1 2027", start: "2027-01-01", end: "2027-03-31" },
    { label: "Q2 2027", start: "2027-04-01", end: "2027-06-30" },
    { label: "Q3 2027", start: "2027-07-01", end: "2027-09-30" },
    { label: "Q4 2027", start: "2027-10-01", end: "2027-12-31" },
  ];
  return labels.map((q) => ({
    ...q,
    left: (daysBetween(CHART_START, parseDate(q.start)) / TOTAL_DAYS) * 100,
    width: (daysBetween(parseDate(q.start), parseDate(q.end)) / TOTAL_DAYS) * 100,
  }));
}

type QuarterCol = ReturnType<typeof getQuarters>[number];

type Pct = { left: number; width: number };

function itemPct(item: { start: string; end: string }): Pct {
  const s = parseDate(item.start);
  const e = parseDate(item.end);
  const left = (daysBetween(CHART_START, s) / TOTAL_DAYS) * 100;
  const width = Math.max((daysBetween(s, e) / TOTAL_DAYS) * 100, 0.5);
  return { left, width };
}

interface FactBar {
  left: number;
  width: number;
}

/**
 * Геометрия «фактической» полосы строки общего ганта (рисуется под плановой).
 * null — если рисовать нечего (не задано `factEnd`).
 *
 * Важно: «опережение по старту» (factStart раньше планового start) на этой полосе
 * НЕ показывается — оно дорисовывается к основной (плановой) колбаске зелёным
 * индикатором раннего старта. Поэтому левый край оранжевой полосы обрезается до
 * планового `start`; сама полоса отражает финиш — раньше срока (короче плана) или
 * с отставанием (длиннее плана). Координаты — проценты на той же шкале, что `itemPct`.
 */
function computeFactBar(item: GanttItem): FactBar | null {
  if (!item.factEnd) return null;
  const factStart = parseDate(item.factStart || item.start);
  const planStart = parseDate(item.start);
  const e = parseDate(item.factEnd);
  if (!isFinite(factStart.getTime()) || !isFinite(planStart.getTime()) || !isFinite(e.getTime())) {
    return null;
  }
  if (e.getTime() < factStart.getTime()) return null;
  // Старт оранжевой полосы — не раньше планового (ранний лид рисуется отдельно).
  const s = factStart.getTime() < planStart.getTime() ? planStart : factStart;
  const vs = s.getTime() < CHART_START.getTime() ? CHART_START : s;
  const ve = e.getTime() > CHART_END.getTime() ? CHART_END : e;
  if (ve.getTime() <= vs.getTime()) return null;
  const left = (daysBetween(CHART_START, vs) / TOTAL_DAYS) * 100;
  const width = Math.max((daysBetween(vs, ve) / TOTAL_DAYS) * 100, 0.5);
  if (left > 100 || left + width < 0) return null;
  return { left, width };
}

type TaskPct =
  | {
      left: number;
      width: number;
      clippedLeft: boolean;
      clippedRight: boolean;
    }
  | null;

/** Позиция задачи на общей шкале; задачи полностью вне диапазона → null. */
function taskPct(task: EditableGanttTask): TaskPct {
  const start = parseDate(task.start);
  const end = parseDate(task.end);
  if (!isFinite(start.getTime()) || !isFinite(end.getTime())) return null;
  const sMs = start.getTime();
  const eMs = end.getTime();
  if (eMs < sMs) return null;
  const chartStartMs = CHART_START.getTime();
  const chartEndMs = CHART_END.getTime();
  if (eMs < chartStartMs || sMs > chartEndMs) return null;
  const visibleStartMs = Math.max(sMs, chartStartMs);
  const visibleEndMs = Math.min(eMs, chartEndMs);
  const leftPct = ((visibleStartMs - chartStartMs) / 86400000 / TOTAL_DAYS) * 100;
  const widthPct = Math.max(((visibleEndMs - visibleStartMs) / 86400000 / TOTAL_DAYS) * 100, 0.3);
  return {
    left: leftPct,
    width: widthPct,
    clippedLeft: sMs < chartStartMs,
    clippedRight: eMs > chartEndMs,
  };
}

/** Дата для input type="date" (YYYY-MM-DD). */
function toDateInputValue(iso: string): string {
  const s = iso.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

function formatRuShortDate(iso: string): string {
  const d = parseDate(iso);
  if (!isFinite(d.getTime())) return iso;
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(d);
}

/** Совсем короткая дата «дд.мм» для подписей внутри колбасок (год — в тултипе). */
function formatRuTinyDate(iso: string): string {
  const d = parseDate(iso);
  if (!isFinite(d.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  }).format(d);
}

/** Простая русскоязычная плюрализация для «дней». 1 день, 2/3/4 дня, 5+ дней. */
function pluralizeDays(n: number): string {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "дня";
  return "дней";
}

/**
 * Считает индикатор «старт с опережением графика» для конкретной полосы
 * общего ганта. Возвращает `null`, если опережения нет, либо данные для
 * визуализации, если фактическая активация (`activatedAt`) была раньше
 * планового начала полосы (`item.start`).
 *
 * Полоса рисуется от `max(activatedAt, CHART_START)` до `item.start`, с
 * шевроном-стрелкой, указывающей влево — на момент активации.
 */
function computeEarlyStart(
  itemStartIso: string,
  activatedAtIso: string | undefined,
): EarlyStartIndicator | null {
  if (!activatedAtIso) return null;
  const planned = parseDate(itemStartIso);
  const actual = parseDate(activatedAtIso);
  if (!isFinite(planned.getTime()) || !isFinite(actual.getTime())) return null;
  if (actual.getTime() >= planned.getTime()) return null;

  const visibleStart = actual.getTime() < CHART_START.getTime() ? CHART_START : actual;
  const visibleEnd = planned.getTime() > CHART_END.getTime() ? CHART_END : planned;
  if (visibleEnd.getTime() <= visibleStart.getTime()) return null;

  const left = (daysBetween(CHART_START, visibleStart) / TOTAL_DAYS) * 100;
  const width = Math.max(
    (daysBetween(visibleStart, visibleEnd) / TOTAL_DAYS) * 100,
    0.4,
  );
  const daysAhead = Math.max(daysBetween(actual, planned), 1);

  return {
    left,
    width,
    daysAhead,
    actualDateLabel: formatRuShortDate(activatedAtIso),
    actualDateTiny: formatRuTinyDate(activatedAtIso),
    plannedDateLabel: formatRuShortDate(itemStartIso),
  };
}

/* ─────────────────────── SortableMainGanttRow ────────────────────────── */

/**
 * Метаданные «старт с опережением графика» для полосы общего ганта.
 * `left` / `width` — проценты на шкале графика; `daysAhead` — насколько раньше
 * планового старта проект был фактически активирован.
 */
interface EarlyStartIndicator {
  left: number;
  width: number;
  daysAhead: number;
  actualDateLabel: string;
  /** Фактическая дата старта в коротком виде «дд.мм» — подпись у левого края лида. */
  actualDateTiny: string;
  plannedDateLabel: string;
}

function SortableMainGanttRow({
  item,
  canEdit,
  quarters,
  todayLeft,
  earlyStart,
  factBar,
  getBarStyle,
  openDateDialog,
  isExpanded,
  hasProject,
  onToggleExpanded,
  timelinePx,
}: {
  item: GanttItem;
  canEdit: boolean;
  quarters: QuarterCol[];
  todayLeft: number | null;
  earlyStart: EarlyStartIndicator | null;
  factBar: FactBar | null;
  getBarStyle: (item: GanttItem) => { left: string; width: string };
  openDateDialog: (item: GanttItem) => void;
  isExpanded: boolean;
  hasProject: boolean;
  onToggleExpanded: () => void;
  /** Текущая ширина шкалы (px) — чтобы решить, влезают ли подписи дат в колбаску. */
  timelinePx: number;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !canEdit,
  });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridColumn: "1 / -1" as const,
    display: "grid",
    gridTemplateColumns: "subgrid",
  };

  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
  const toggleLabel = hasProject
    ? isExpanded
      ? `Свернуть план проекта: ${item.title}`
      : `Развернуть план проекта: ${item.title}`
    : "Карточка проекта не найдена";
  const toggleTitle = hasProject
    ? isExpanded
      ? "Свернуть план проекта"
      : "Развернуть план проекта"
    : "Карточка проекта не найдена — раскрыть нечего";

  const barColor = STATUS_COLORS[item.status] ?? STATUS_COLORS.planned;
  const barHeight = isExpanded ? BAR_HEIGHT_EXPANDED_PX : BAR_HEIGHT_PX;

  // Когда есть «фактическая» полоса, плановая и фактическая колбаски рисуются
  // одинаковой толщины парой и центрируются вокруг середины строки: план сверху,
  // факт снизу с зазором GANTT_PAIR_GAP. Без факта плановая колбаска центрируется
  // как обычно. Зелёный «ранний старт» — левое продолжение плановой колбаски —
  // выравнивается по её центру (а не по центру строки).
  const earlyHeight = Math.max(barHeight - 2, 6);
  const planMarginTop = factBar ? -(barHeight + GANTT_PAIR_GAP / 2) : -barHeight / 2;
  const factMarginTop = GANTT_PAIR_GAP / 2;
  const earlyMarginTop = planMarginTop + barHeight / 2 - earlyHeight / 2;

  // Подписи дат «дд.мм … дд.мм» внутри колбасок. Показываем, только если полоса
  // в пикселях шире порога — иначе подписи налезают друг на друга (даты всегда
  // доступны в тултипе). Ширину в px считаем из процентов и текущей шкалы.
  const planWidthPx = (itemPct(item).width / 100) * timelinePx;
  const showPlanDates = planWidthPx >= GANTT_BAR_LABEL_MIN_PX;
  const factWidthPx = factBar ? (factBar.width / 100) * timelinePx : 0;
  const showFactDates = !!factBar && factWidthPx >= GANTT_BAR_LABEL_MIN_PX;
  const planStartLabel = formatRuTinyDate(item.start);
  const planEndLabel = formatRuTinyDate(item.end);
  const factStartLabel = formatRuTinyDate(item.factStart || item.start);
  const factEndLabel = item.factEnd ? formatRuTinyDate(item.factEnd) : "";
  // Старт факта раньше плана → оранжевая полоса обрезана слева до планового
  // старта, а фактическую дату старта подписывает зелёный лид. Поэтому левую
  // подпись на оранжевой в этом случае не дублируем (она встала бы у планового
  // края = совпала бы с подписью плановой полосы сверху).
  const factStartedEarly =
    !!item.factStart &&
    !!item.factEnd &&
    parseDate(item.factStart).getTime() < parseDate(item.start).getTime();
  const showFactStartLabel = showFactDates && !factStartedEarly;
  // Подпись фактической даты старта слева от зелёного лида показываем, только
  // если слева хватает места (иначе текст упрётся в край шкалы/колонку названий).
  const showEarlyStartDate =
    !!earlyStart &&
    !!earlyStart.actualDateTiny &&
    (earlyStart.left / 100) * timelinePx >= 34;
  // Базовый стиль подписи: белый текст с тенью читается на всех цветах колбасок.
  const barDateBaseStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 9,
    lineHeight: 1,
    fontWeight: 600,
    color: "#fff",
    textShadow: "0 1px 1.5px rgba(0, 0, 0, 0.45)",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.2px",
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      className={cn(
        "transition-colors",
        isDragging && "relative z-[21] bg-background/95 shadow-md ring-1 ring-border",
        isExpanded && "bg-muted/20",
      )}
    >
      <div
        className={cn(
          "sticky left-0 z-[14] flex items-start gap-1 border-r border-border bg-background px-2 py-2 text-xs leading-snug text-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]",
          !isExpanded && "border-b",
          isExpanded && "bg-muted/30",
        )}
        style={{
          width: GANTT_LABEL_COL_WIDTH,
          minWidth: GANTT_LABEL_COL_WIDTH,
          wordBreak: "break-word",
        }}
      >
        {canEdit ? (
          <button
            type="button"
            className="mt-0.5 flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded p-0 text-muted-foreground hover:bg-muted active:cursor-grabbing"
            aria-label="Перетащить строку"
            title="Перетащить"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasProject) onToggleExpanded();
          }}
          disabled={!hasProject}
          className={cn(
            "mt-0.5 flex h-7 w-5 shrink-0 items-center justify-center rounded p-0 transition-colors",
            hasProject
              ? isExpanded
                ? "bg-[var(--nn-blue-pale)] text-[var(--nn-blue)]"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
              : "cursor-not-allowed text-muted-foreground opacity-40",
          )}
          aria-label={toggleLabel}
          aria-expanded={isExpanded}
          title={toggleTitle}
        >
          <ChevronIcon className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
        <span className="min-w-0 flex-1" title={item.title}>
          {item.title}
        </span>
        {canEdit ? (
          <span className="flex shrink-0 items-start gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground/80 hover:text-foreground"
              title="Сроки полосы (начало и окончание)"
              aria-label={`Сроки для: ${item.title}`}
              onClick={(e) => {
                e.stopPropagation();
                openDateDialog(item);
              }}
            >
              <CalendarRange className="h-3.5 w-3.5" />
            </Button>
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          "relative z-0 min-h-[40px] min-w-0 bg-background/50",
          !isExpanded && "border-b border-border/60",
          isExpanded && "bg-muted/15",
        )}
        style={{ minWidth: GANTT_TIMELINE_MIN_WIDTH }}
      >
        {quarters.map((q) => (
          <div
            key={`${item.id}-${q.label}`}
            className="pointer-events-none absolute top-0 bottom-0 border-l border-border/35"
            style={{ left: `${q.left}%` }}
          />
        ))}
        {todayLeft !== null ? (
          <div
            className="pointer-events-none absolute top-0 bottom-0 z-10 w-px bg-rose-500/45"
            style={{ left: `${todayLeft}%` }}
          />
        ) : null}

        {/* Индикатор «старт с опережением графика»: тонкая зелёная полоска
            от фактической даты активации до запланированного старта полосы,
            с шевроном-стрелкой слева. */}
        {earlyStart ? (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <span
                className="gantt-early-start absolute z-[15]"
                style={{
                  left: `${earlyStart.left}%`,
                  width: `${earlyStart.width}%`,
                  top: "50%",
                  height: earlyHeight,
                  marginTop: earlyMarginTop,
                }}
                aria-label={`Старт с опережением: ${earlyStart.daysAhead} дн.`}
              >
                {/* Сама полоска — пастельно-зелёная с диагональной штриховкой,
                    чтобы визуально отличалась от плановой части бара. */}
                <span
                  className="pointer-events-none block h-full w-full rounded-[2px]"
                  style={{
                    background:
                      "repeating-linear-gradient(135deg, rgba(16, 185, 129, 0.55) 0 4px, rgba(16, 185, 129, 0.85) 4px 8px)",
                    boxShadow: "inset 0 0 0 1px rgba(16, 185, 129, 0.55)",
                  }}
                />
                {/* Левый шеврон-стрелка, указывающая на дату активации. */}
                <span
                  className="pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center justify-center"
                  style={{ left: -2, width: 14, height: 14 }}
                >
                  <ChevronLeft
                    className="h-3.5 w-3.5"
                    strokeWidth={2.5}
                    style={{ color: "rgb(5, 150, 105)" }}
                    aria-hidden
                  />
                </span>
                {/* Подпись фактической даты старта — слева от лида, тёмно-зелёным
                    (читается на светлом фоне шкалы). */}
                {showEarlyStartDate ? (
                  <span
                    className="pointer-events-none absolute"
                    style={{
                      right: "100%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      marginRight: 6,
                      fontSize: 9,
                      lineHeight: 1,
                      fontWeight: 700,
                      color: "rgb(4, 120, 87)",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: "0.2px",
                    }}
                  >
                    {earlyStart.actualDateTiny}
                  </span>
                ) : null}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[22rem] text-xs">
              <div className="space-y-0.5">
                <div className="font-semibold text-emerald-700">
                  Старт с опережением графика
                </div>
                <div className="text-muted-foreground">
                  Фактически: {earlyStart.actualDateLabel} · по плану:{" "}
                  {earlyStart.plannedDateLabel}
                </div>
                <div className="text-foreground">
                  Раньше плана на{" "}
                  <span className="font-medium">
                    {earlyStart.daysAhead}{" "}
                    {pluralizeDays(earlyStart.daysAhead)}
                  </span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasProject) onToggleExpanded();
          }}
          disabled={!hasProject}
          aria-label={toggleLabel}
          title={toggleTitle}
          className={cn(
            "gantt-bar absolute z-20 cursor-pointer rounded-[2px] border-0 p-0 transition-all duration-200",
            !hasProject && "cursor-not-allowed",
          )}
          style={{
            ...getBarStyle(item),
            top: "50%",
            height: barHeight,
            marginTop: planMarginTop,
            background: barColor,
            boxShadow: isExpanded
              ? `0 0 0 2px ${barColor}4D, 0 6px 14px ${barColor}55`
              : undefined,
            filter: isExpanded ? "saturate(1.1) brightness(1.05)" : undefined,
            overflow: "hidden",
          }}
        >
          {showPlanDates ? (
            <>
              <span style={{ ...barDateBaseStyle, left: 5 }}>{planStartLabel}</span>
              <span style={{ ...barDateBaseStyle, right: 5 }}>{planEndLabel}</span>
            </>
          ) : null}
        </button>

        {/* «Фактическая» полоса — той же толщины, что плановая, рисуется под ней
            приглушённым оранжевым. Отражает финиш факта: короче плана — закончили
            раньше срока, длиннее — отставание. Левый край обрезан до планового старта
            (ранний старт показан зелёным лидом на плановой колбаске). */}
        {factBar ? (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <span
                className="absolute z-[18] cursor-default rounded-[2px]"
                style={{
                  left: `${factBar.left}%`,
                  width: `${factBar.width}%`,
                  top: "50%",
                  height: barHeight,
                  marginTop: factMarginTop,
                  background: FACT_BAR_COLOR,
                  boxShadow: `inset 0 0 0 1px ${FACT_BAR_BORDER}`,
                  overflow: "hidden",
                }}
                aria-label="Фактические сроки"
              >
                {showFactDates ? (
                  <>
                    {showFactStartLabel ? (
                      <span style={{ ...barDateBaseStyle, left: 5 }}>{factStartLabel}</span>
                    ) : null}
                    <span style={{ ...barDateBaseStyle, right: 5 }}>{factEndLabel}</span>
                  </>
                ) : null}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[22rem] text-xs">
              <div className="space-y-0.5">
                <div className="font-semibold" style={{ color: "rgb(176, 102, 44)" }}>
                  Фактические сроки
                </div>
                <div className="text-muted-foreground">
                  Факт: {formatRuShortDate(item.factStart || item.start)} →{" "}
                  {formatRuShortDate(item.factEnd!)}
                </div>
                <div className="text-muted-foreground">
                  План: {formatRuShortDate(item.start)} → {formatRuShortDate(item.end)}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}

/* ────────────────────── Project inset (врезка) ───────────────────────── */

function InsetTaskRow({
  task,
  onOpen,
  parentLeft,
  parentWidth,
}: {
  task: EditableGanttTask;
  onOpen: () => void;
  /** Границы родительской колбаски в % — чтобы подсвечивать «попадание» в интервал. */
  parentLeft: number;
  parentWidth: number;
}) {
  const pos = taskPct(task);
  const color = INSET_STATUS_COLORS[task.status];
  const tooltipSide = task.status === "planned" ? "bottom" : "top";

  const inEnvelope = useMemo(() => {
    if (!pos) return false;
    const pR = parentLeft + parentWidth;
    const tR = pos.left + pos.width;
    // пересечение с родительским интервалом (нестрого внутри)
    return pos.left >= parentLeft - 0.5 && tR <= pR + 0.5;
  }, [pos, parentLeft, parentWidth]);

  const dates = `${formatRuShortDate(task.start)} → ${formatRuShortDate(task.end)}`;

  return (
    <React.Fragment>
      <div
        className="sticky left-0 z-[13] flex h-7 items-center gap-1.5 border-b border-border/20 bg-muted/25 px-3 text-[11px] text-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]"
        style={{ width: GANTT_LABEL_COL_WIDTH, minWidth: GANTT_LABEL_COL_WIDTH }}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/40"
          style={{ background: color }}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate" title={task.title}>
          {task.title}
        </span>
        <span
          className="shrink-0 rounded-full px-1.5 py-[1px] text-[9px] font-medium uppercase tracking-wide"
          style={{ background: `${color}22`, color }}
        >
          {INSET_STATUS_LABELS[task.status]}
        </span>
      </div>
      <div
        className="relative z-[2] h-7 min-w-0 border-b border-border/20"
        style={{ minWidth: GANTT_TIMELINE_MIN_WIDTH }}
      >
        {pos ? (
          <Tooltip delayDuration={120}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onOpen}
                aria-label={`${task.title}: ${dates}`}
                className={cn(
                  "group/bar absolute top-1/2 -translate-y-1/2 cursor-pointer rounded-[3px] border p-0 text-left transition-all",
                  "hover:brightness-110 hover:-translate-y-[calc(50%+1px)] hover:shadow-md",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nn-blue)] focus-visible:ring-offset-1",
                )}
                style={{
                  left: `${pos.left}%`,
                  width: `${pos.width}%`,
                  height: 11,
                  background: color,
                  borderColor: `${color}cc`,
                  boxShadow: inEnvelope
                    ? `0 1px 2px ${color}55, inset 0 0 0 1px rgba(255,255,255,0.25)`
                    : `0 1px 2px ${color}55, inset 0 0 0 1px rgba(255,255,255,0.25), 0 0 0 1px ${color}44`,
                  opacity: inEnvelope ? 1 : 0.95,
                }}
              >
                {task.progress > 0 ? (
                  <span
                    className="pointer-events-none absolute inset-y-0 left-0 rounded-[3px] bg-black/20"
                    style={{ width: `${Math.min(100, Math.max(0, task.progress))}%` }}
                    aria-hidden
                  />
                ) : null}
                {pos.clippedLeft ? (
                  <span
                    className="pointer-events-none absolute -left-[6px] top-1/2 block h-0 w-0 -translate-y-1/2 border-y-[5px] border-r-[6px] border-y-transparent"
                    style={{ borderRightColor: color }}
                    aria-hidden
                  />
                ) : null}
                {pos.clippedRight ? (
                  <span
                    className="pointer-events-none absolute -right-[6px] top-1/2 block h-0 w-0 -translate-y-1/2 border-y-[5px] border-l-[6px] border-y-transparent"
                    style={{ borderLeftColor: color }}
                    aria-hidden
                  />
                ) : null}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side={tooltipSide}
              sideOffset={8}
              className="max-w-[22rem] border border-border bg-popover text-popover-foreground shadow-xl"
            >
              <div className="space-y-1 text-left">
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: color }}
                    aria-hidden
                  />
                  <span className="text-[13px] font-semibold leading-tight">{task.title}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {INSET_STATUS_LABELS[task.status]} · {dates}
                  {task.progress > 0 ? ` · ${Math.round(task.progress)}%` : ""}
                </div>
                {task.assignees.length > 0 ? (
                  <div className="text-[11px] text-muted-foreground">
                    Исполнители: {task.assignees.join(", ")}
                  </div>
                ) : null}
                <div className="pt-1 text-[10px] italic text-muted-foreground">
                  Клик — открыть задачу в карточке проекта
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] italic text-muted-foreground">
            Задача вне видимого диапазона ({formatRuShortDate(task.start)} → {formatRuShortDate(task.end)})
          </span>
        )}
      </div>
    </React.Fragment>
  );
}

function ProjectInset({
  project,
  projectId,
  quarters,
  todayLeft,
  parent,
}: {
  project: EditableProject | null;
  projectId: string;
  quarters: QuarterCol[];
  todayLeft: number | null;
  parent: { item: GanttItem; pct: Pct };
}) {
  const [, navigate] = useLocation();

  if (!project) {
    return (
      <div
        className="grid border-b border-border/50 bg-muted/30"
        style={{
          gridTemplateColumns: `${GANTT_LABEL_COL_WIDTH}px minmax(${GANTT_TIMELINE_MIN_WIDTH}px, 1fr)`,
        }}
      >
        <div
          className="sticky left-0 z-[13] border-r border-border bg-muted/40 px-3 py-3 text-[11px] italic text-muted-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]"
          style={{ width: GANTT_LABEL_COL_WIDTH, minWidth: GANTT_LABEL_COL_WIDTH }}
        >
          Карточка проекта «{projectId}» не найдена в воркспейсе.
        </div>
        <div style={{ minWidth: GANTT_TIMELINE_MIN_WIDTH }} />
      </div>
    );
  }

  const sortedTasks = [...project.ganttTasks].sort(compareGanttTasksDisplayOrder);
  const parentColor = STATUS_COLORS[parent.item.status] ?? STATUS_COLORS.planned;

  const byStatus = sortedTasks.reduce(
    (acc, t) => {
      acc[t.status] = (acc[t.status] ?? 0) + 1;
      return acc;
    },
    { active: 0, behind: 0, planned: 0, done: 0 } as Record<EditableGanttTask["status"], number>,
  );

  return (
    <div
      className="relative grid border-b border-border/50"
      style={{
        gridTemplateColumns: `${GANTT_LABEL_COL_WIDTH}px minmax(${GANTT_TIMELINE_MIN_WIDTH}px, 1fr)`,
        background: `linear-gradient(180deg, hsl(var(--muted) / 0.5) 0%, hsl(var(--muted) / 0.15) 100%)`,
      }}
    >
      {/* overlay над правой колонкой: envelope + today + quarter guides + connectors */}
      <div
        className="pointer-events-none absolute top-0 bottom-0 z-0"
        style={{ left: GANTT_LABEL_COL_WIDTH, right: 0 }}
      >
        <div className="relative h-full">
          {quarters.map((q) => (
            <div
              key={`inset-q-${q.label}`}
              className="absolute top-0 bottom-0 border-l border-border/25"
              style={{ left: `${q.left}%` }}
            />
          ))}
          {/* parent envelope */}
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: `${parent.pct.left}%`,
              width: `${parent.pct.width}%`,
              background: `linear-gradient(180deg, ${parentColor}22 0%, ${parentColor}0A 100%)`,
            }}
          />
          {/* connectors at parent edges */}
          <div
            className="absolute top-0 bottom-0 w-0 border-l border-dashed"
            style={{ left: `${parent.pct.left}%`, borderColor: `${parentColor}aa` }}
          />
          <div
            className="absolute top-0 bottom-0 w-0 border-l border-dashed"
            style={{
              left: `${parent.pct.left + parent.pct.width}%`,
              borderColor: `${parentColor}aa`,
            }}
          />
          {/* today line */}
          {todayLeft !== null ? (
            <div
              className="absolute top-0 bottom-0 w-px bg-rose-500/40"
              style={{ left: `${todayLeft}%` }}
            />
          ) : null}
        </div>
      </div>

      {/* inset header: left */}
      <div
        className="sticky left-0 z-[13] flex flex-col gap-1 border-b border-r border-border bg-muted/40 px-3 py-2 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]"
        style={{ width: GANTT_LABEL_COL_WIDTH, minWidth: GANTT_LABEL_COL_WIDTH }}
      >
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          План-график проекта
        </span>
        <span
          className="break-words text-[12px] font-semibold leading-snug text-foreground"
          title={project.title}
        >
          {project.title}
        </span>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
          {(Object.keys(INSET_STATUS_COLORS) as EditableGanttTask["status"][]).map((k) =>
            byStatus[k] > 0 ? (
              <span key={k} className="inline-flex items-center gap-1">
                <span
                  className="h-2 w-2 rounded-sm"
                  style={{ background: INSET_STATUS_COLORS[k] }}
                />
                {INSET_STATUS_LABELS[k]} · {byStatus[k]}
              </span>
            ) : null,
          )}
        </div>
        <Link
          href={`/project/${project.id}`}
          className="mt-1 inline-flex items-center gap-1 self-start text-[11px] font-medium text-[var(--nn-blue)] hover:underline"
        >
          <ExternalLink className="h-3 w-3" /> Открыть карточку
        </Link>
      </div>
      {/* inset header: right — декоративный трек, тут только для высоты шапки */}
      <div
        className="relative z-[1] h-auto min-w-0 border-b border-border/30"
        style={{ minWidth: GANTT_TIMELINE_MIN_WIDTH }}
      >
        {/* небольшие подписи кварталов во врезке — необязательно, но добавляет ориентир */}
        <div className="pointer-events-none absolute inset-0 flex items-end pb-0.5 pl-0.5">
          {quarters.map((q) => (
            <span
              key={`inset-qlabel-${q.label}`}
              className="absolute text-[9px] uppercase tracking-wide text-muted-foreground/60"
              style={{ left: `calc(${q.left}% + 4px)`, bottom: 2 }}
            >
              {q.label}
            </span>
          ))}
        </div>
      </div>

      {/* task rows */}
      {sortedTasks.length === 0 ? (
        <>
          <div
            className="sticky left-0 z-[13] flex items-center border-r border-border bg-muted/30 px-3 py-4 text-[11px] italic text-muted-foreground shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]"
            style={{ width: GANTT_LABEL_COL_WIDTH, minWidth: GANTT_LABEL_COL_WIDTH }}
          >
            У проекта пока нет задач в план-графике.
          </div>
          <div
            className="relative z-[2] flex min-w-0 items-center px-3 text-[11px] italic text-muted-foreground"
            style={{ minWidth: GANTT_TIMELINE_MIN_WIDTH }}
          >
            <Link
              href={`/project/${project.id}`}
              className="text-[var(--nn-blue)] hover:underline"
            >
              Открыть карточку и добавить задачи →
            </Link>
          </div>
        </>
      ) : (
        sortedTasks.map((task) => (
          <InsetTaskRow
            key={task.id}
            task={task}
            parentLeft={parent.pct.left}
            parentWidth={parent.pct.width}
            onOpen={() =>
              navigate(`/project/${project.id}?task=${encodeURIComponent(task.id)}`)
            }
          />
        ))
      )}
    </div>
  );
}

/* ───────────────────────────── main chart ──────────────────────────── */

export default function GanttChart() {
  const {
    ganttItems,
    roadmapPhases,
    projects,
    canEdit,
    updateGanttItem,
    updateProject,
    reorderGanttItemsForPhase,
  } = useProjects();

  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());

  const [dateDialogItem, setDateDialogItem] = useState<GanttItem | null>(null);
  const [dateDialogStart, setDateDialogStart] = useState("");
  const [dateDialogEnd, setDateDialogEnd] = useState("");
  const [dateDialogFactStart, setDateDialogFactStart] = useState("");
  const [dateDialogFactEnd, setDateDialogFactEnd] = useState("");
  const [dateDialogError, setDateDialogError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Текущая ширина шкалы (px) — нужна, чтобы решать, влезают ли подписи дат
  // внутрь колбасок. Шкала = ширина контейнера минус колонка названий (но не
  // меньше минимальной ширины таймлайна).
  const [timelinePx, setTimelinePx] = useState(GANTT_TIMELINE_MIN_WIDTH);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => {
      const px =
        Math.max(el.clientWidth, GANTT_LABEL_COL_WIDTH + GANTT_TIMELINE_MIN_WIDTH) -
        GANTT_LABEL_COL_WIDTH;
      setTimelinePx(px);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const quarters = getQuarters();

  // Реальная сегодняшняя дата (а НЕ хардкод) — позиция линии «сегодня»
  // должна меняться в течение программы. Раз в час мы перепроверяем, не
  // настал ли следующий день, чтобы линия плавно переезжала, если страница
  // открыта долго.
  const [today, setToday] = useState<Date>(() => todayMidnight());
  useEffect(() => {
    const tick = () => {
      const next = todayMidnight();
      setToday((prev) => (prev.getTime() === next.getTime() ? prev : next));
    };
    const interval = window.setInterval(tick, 60 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);
  const todayLeft = (daysBetween(CHART_START, today) / TOTAL_DAYS) * 100;
  // Линия «сегодня» рисуется только если дата попадает в диапазон графика.
  const todayInRange = todayLeft >= 0 && todayLeft <= 100;
  const todayLabelLong = formatTodayLong(today);

  const phaseDefs = roadmapPhasesSorted(roadmapPhases);

  const projectById = useMemo(() => {
    const m = new Map<string, EditableProject>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const ganttBarCountByProjectId = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of ganttItems) {
      m.set(g.projectId, (m.get(g.projectId) ?? 0) + 1);
    }
    return m;
  }, [ganttItems]);

  const toggleExpanded = useCallback((projectId: string) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  // Если проект исчез (например, был удалён) — убрать его из раскрытий.
  useEffect(() => {
    setExpandedProjectIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (projectById.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [projectById]);

  function getBarStyle(item: GanttItem) {
    const { left, width } = itemPct(item);
    return { left: `${left}%`, width: `${width}%` };
  }

  function openDateDialog(item: GanttItem) {
    setDateDialogError(null);
    setDateDialogItem(item);
    setDateDialogStart(toDateInputValue(item.start));
    setDateDialogEnd(toDateInputValue(item.end));
    setDateDialogFactStart(item.factStart ? toDateInputValue(item.factStart) : "");
    setDateDialogFactEnd(item.factEnd ? toDateInputValue(item.factEnd) : "");
  }

  function saveDateDialog() {
    if (!dateDialogItem) return;
    const start = dateDialogStart.trim();
    const end = dateDialogEnd.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
      setDateDialogError("Укажите даты начала и окончания в формате ГГГГ-ММ-ДД.");
      return;
    }
    if (start > end) {
      setDateDialogError("Дата начала не может быть позже даты окончания.");
      return;
    }
    const factStart = dateDialogFactStart.trim();
    const factEnd = dateDialogFactEnd.trim();
    if (factStart && factEnd && factStart > factEnd) {
      setDateDialogError("Факт: дата начала не может быть позже окончания.");
      return;
    }
    setDateDialogError(null);
    updateGanttItem(dateDialogItem.id, {
      start,
      end,
      factStart: factStart || undefined,
      factEnd: factEnd || undefined,
    });

    const barsForProject = ganttBarCountByProjectId.get(dateDialogItem.projectId) ?? 0;
    const projectExists = projects.some((p) => p.id === dateDialogItem.projectId);
    if (barsForProject === 1 && projectExists) {
      updateProject(dateDialogItem.projectId, { startDate: start, targetDate: end });
    }

    setDateDialogItem(null);
  }

  /** Удалить фактическую полосу строки (если её добавили по ошибке). Плановая остаётся. */
  function deleteFactBar() {
    if (!dateDialogItem) return;
    if (!window.confirm("Удалить фактическую полосу этой строки? Плановая полоса останется.")) return;
    updateGanttItem(dateDialogItem.id, { factStart: undefined, factEnd: undefined });
    setDateDialogFactStart("");
    setDateDialogFactEnd("");
    setDateDialogError(null);
    setDateDialogItem(null);
  }

  const gridMinWidth = GANTT_LABEL_COL_WIDTH + GANTT_TIMELINE_MIN_WIDTH;

  return (
    <div className="relative">
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div
            key={key}
            className="flex items-center gap-1.5 text-xs"
            style={{ color: "var(--muted-foreground)" }}
          >
            <span
              className="w-3 h-3 rounded-sm inline-block"
              style={{ background: STATUS_COLORS[key] }}
            />
            {label}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="inline-block h-3 w-4 rounded-[2px]"
            style={{
              background:
                "repeating-linear-gradient(135deg, rgba(16, 185, 129, 0.55) 0 3px, rgba(16, 185, 129, 0.85) 3px 6px)",
            }}
          />
          Старт с опережением графика
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="inline-block h-3 w-4 rounded-[2px]"
            style={{
              background: FACT_BAR_COLOR,
              boxShadow: `inset 0 0 0 1px ${FACT_BAR_BORDER}`,
            }}
          />
          Фактические сроки
        </div>
        <div className="flex items-baseline gap-1.5 text-xs ml-auto">
          <span
            className="w-px h-3 inline-block bg-rose-500/70 self-center"
            aria-hidden
          />
          <span className="text-muted-foreground">Сегодня</span>
          {todayInRange ? (
            <>
              <span className="text-muted-foreground/60" aria-hidden>·</span>
              <span className="font-medium text-foreground tabular-nums">
                {todayLabelLong}
              </span>
            </>
          ) : null}
        </div>
      </div>


      <div ref={scrollRef} className="overflow-x-auto rounded-md border border-border/80 bg-muted/15">
        <div className="relative block w-full min-w-0">
          <div
            className="grid w-full"
            style={{
              minWidth: gridMinWidth,
              width: "100%",
              gridTemplateColumns: `${GANTT_LABEL_COL_WIDTH}px minmax(${GANTT_TIMELINE_MIN_WIDTH}px, 1fr)`,
            }}
          >
            <div
              className="sticky left-0 z-[16] flex items-center gap-1 border-b border-r border-border bg-background px-2 py-1.5 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.07)]"
              style={{ width: GANTT_LABEL_COL_WIDTH, minWidth: GANTT_LABEL_COL_WIDTH }}
            >
              {canEdit ? <span className="w-5 shrink-0" aria-hidden /> : null}
            </div>
            <div className="relative z-0 h-8 min-w-0 overflow-visible border-b border-border/70 bg-muted/25">
              {quarters.map((q) => (
                <div
                  key={q.label}
                  className="absolute top-0 flex h-full items-center justify-center border-l border-border/50 text-[11px] font-medium text-muted-foreground/90"
                  style={{
                    left: `${q.left}%`,
                    width: `${q.width}%`,
                  }}
                >
                  {q.label}
                </div>
              ))}
            </div>

            {phaseDefs.map((phaseInfo) => {
              const items = ganttItems.filter((i) => i.phaseId === phaseInfo.id);
              if (!items.length) return null;
              return (
                <div key={phaseInfo.id} style={{ display: "contents" }}>
                  <div
                    className="col-span-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/60 px-2 py-1 text-[11px] font-semibold tracking-tight"
                    style={{
                      background: phaseRowBackground(phaseInfo.color),
                      borderLeft: `2px solid ${phaseInfo.color}`,
                      color: "hsl(222 18% 32%)",
                      gridColumn: "1 / -1",
                    }}
                  >
                    <span>{phaseInfo.label}</span>
                    <span className="font-normal opacity-80">{phaseInfo.period}</span>
                    {phaseInfo.title ? (
                      <span className="w-full font-normal opacity-70 sm:w-auto sm:pl-1">
                        — {phaseInfo.title}
                      </span>
                    ) : null}
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event: DragEndEvent) => {
                      const { active, over } = event;
                      if (!over || !canEdit || active.id === over.id) return;
                      const ids = items.map((i) => i.id);
                      const oldIndex = ids.indexOf(String(active.id));
                      const newIndex = ids.indexOf(String(over.id));
                      if (oldIndex < 0 || newIndex < 0) return;
                      reorderGanttItemsForPhase(phaseInfo.id, arrayMove(ids, oldIndex, newIndex));
                    }}
                  >
                    <SortableContext
                      items={items.map((i) => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {items.map((item) => {
                        const project = projectById.get(item.projectId) ?? null;
                        const isExpanded = expandedProjectIds.has(item.projectId);
                        const pct = itemPct(item);
                        // «Ранний старт» (зелёный лид на основной колбаске) берём из
                        // самой ранней доступной фактической даты начала: либо
                        // фактический старт строки (`factStart`), либо активация проекта
                        // (`activatedAt`). ISO-строки сортируются хронологически.
                        const earlyStartSource = [item.factStart, project?.activatedAt]
                          .filter((d): d is string => Boolean(d))
                          .sort()[0];
                        const earlyStart = computeEarlyStart(item.start, earlyStartSource);
                        const factBar = computeFactBar(item);
                        return (
                          <React.Fragment key={item.id}>
                            <SortableMainGanttRow
                              item={item}
                              canEdit={canEdit}
                              quarters={quarters}
                              todayLeft={todayInRange ? todayLeft : null}
                              earlyStart={earlyStart}
                              factBar={factBar}
                              getBarStyle={getBarStyle}
                              openDateDialog={openDateDialog}
                              isExpanded={isExpanded}
                              hasProject={project !== null}
                              timelinePx={timelinePx}
                              onToggleExpanded={() => toggleExpanded(item.projectId)}
                            />
                            <AnimatePresence initial={false}>
                              {isExpanded ? (
                                <motion.div
                                  key={`inset-${item.id}`}
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{
                                    height: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
                                    opacity: { duration: 0.22 },
                                  }}
                                  style={{
                                    gridColumn: "1 / -1",
                                    overflow: "hidden",
                                  }}
                                >
                                  <ProjectInset
                                    project={project}
                                    projectId={item.projectId}
                                    quarters={quarters}
                                    todayLeft={todayInRange ? todayLeft : null}
                                    parent={{ item, pct }}
                                  />
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </React.Fragment>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog
        open={dateDialogItem !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDateDialogItem(null);
            setDateDialogError(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Сроки полосы</DialogTitle>
            <p className="text-sm text-muted-foreground font-normal pt-1">
              <span className="text-foreground font-medium">{dateDialogItem?.title ?? ""}</span>
            </p>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="gantt-date-start">Начало</Label>
              <Input
                id="gantt-date-start"
                type="date"
                value={dateDialogStart}
                onChange={(e) => setDateDialogStart(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="gantt-date-end">Окончание</Label>
              <Input
                id="gantt-date-end"
                type="date"
                value={dateDialogEnd}
                onChange={(e) => setDateDialogEnd(e.target.value)}
              />
            </div>

            <div className="space-y-2 rounded-md border border-border/70 bg-muted/20 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Фактические сроки (опционально)
              </p>
              <p className="text-[11px] leading-snug text-muted-foreground">
                Если сроки поехали — укажите факт. Под плановой полосой появится «фактическая».
                Достаточно одной даты окончания (старт тогда возьмётся плановый).
              </p>
              <div className="grid gap-2">
                <Label htmlFor="gantt-fact-start">Старт (факт)</Label>
                <Input
                  id="gantt-fact-start"
                  type="date"
                  value={dateDialogFactStart}
                  onChange={(e) => setDateDialogFactStart(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="gantt-fact-end">Окончание (факт)</Label>
                <Input
                  id="gantt-fact-end"
                  type="date"
                  value={dateDialogFactEnd}
                  onChange={(e) => setDateDialogFactEnd(e.target.value)}
                />
              </div>
              {dateDialogFactStart || dateDialogFactEnd ? (
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => {
                    setDateDialogFactStart("");
                    setDateDialogFactEnd("");
                  }}
                >
                  Очистить фактические сроки
                </button>
              ) : null}
            </div>
            {dateDialogItem &&
            (ganttBarCountByProjectId.get(dateDialogItem.projectId) ?? 0) > 1 ? (
              <p className="text-xs text-muted-foreground">
                У проекта несколько полос на этой диаграмме — после сохранения даты в паспорте
                проекта <strong>не</strong> меняются (их можно править на странице инициативы).
              </p>
            ) : dateDialogItem && !projects.some((p) => p.id === dateDialogItem.projectId) ? (
              <p className="text-xs text-muted-foreground">
                Проект с id «{dateDialogItem.projectId}» не найден в воркспейсе — обновится только
                полоса ганта.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                У проекта одна полоса на ганте: даты также запишутся в паспорт как «Начало» и
                «Целевая дата».
              </p>
            )}
            {dateDialogError ? (
              <p className="text-sm text-destructive">{dateDialogError}</p>
            ) : null}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {dateDialogItem?.factEnd ? (
              <Button
                type="button"
                variant="destructive"
                className="sm:mr-auto"
                onClick={deleteFactBar}
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Удалить факт. полосу
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDateDialogItem(null);
                setDateDialogError(null);
              }}
            >
              Отмена
            </Button>
            <Button type="button" onClick={saveDateDialog}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
